/**
 * The view a grid remembers between visits: which rows, not how the table looks.
 *
 * Shared by both frontends — the Knockout bundle compiles this subtree — because the Knockout host
 * is what reads it out of the query string and `localStorage` and writes it back, while the React
 * table is what produces it. Keep it dependency-free and JSX-free: the legacy `tsc` compiles it
 * under its own es5/amd config, and this half must stay testable without a browser.
 */

/**
 * Wire names, as they appear in a link and in a stored entry.
 *
 * `show_archived` is no longer written — archived trials are a filter condition now — but is still
 * recognised: a link from before that change is a view, and must not be mistaken for a first visit
 * and given the first visit's default filter, which would hide exactly what that link asked to see.
 */
const VIEW_KEYS = ['search', 'show_archived', 'filter', 'sort'] as const;

/** The same view in the shape callers work with. */
export interface GridViewState {
  search: string;
  filter: string;
  sort: string[];
}

const asText = (value: unknown): string => (typeof value === 'string' ? value : '');

const asTexts = (value: unknown): string[] => {
  if (typeof value === 'string') {
    return [value];
  }
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : [];
};

const fieldsOf = (source: unknown): Record<string, unknown> =>
  typeof source === 'object' && source !== null ? (source as Record<string, unknown>) : {};

/**
 * Whether these parameters carry a view at all.
 *
 * Named keys rather than "any key at all": a link with a tracking parameter, or an old bookmark
 * naming the flat filter parameters the classic list used, would otherwise count as an empty view
 * and so discard the one this grid was left with — showing everything, then overwriting the
 * address it was opened from.
 */
export function carriesGridView(source: unknown): boolean {
  const fields = fieldsOf(source);
  return VIEW_KEYS.some((key) => fields[key] !== undefined);
}

/**
 * Reads a view from anything shaped like one.
 *
 * Every field is checked rather than asserted: both sources are user-editable — a hand-typed query
 * string can repeat a key (`?filter=a&filter=b`, which arrives as an array) or drop one, and a
 * stored entry holds whatever JSON was last written there, possibly by an older build.
 */
export function readGridViewState(source: unknown): GridViewState {
  const fields = fieldsOf(source);
  return {
    search: asText(fields.search),
    filter: asText(fields.filter),
    sort: asTexts(fields.sort),
  };
}

/**
 * The filter a list with an Archived column opens with on a first visit — nothing stored, nothing
 * in the link: archived rows hidden, as the "Show archived" switch it replaced defaulted to.
 *
 * A condition like any other: visible in the filter panel, removable, and once removed the view is
 * stored without it. `field` and `value` are the Archived column's id and its "No" choice, which
 * each list's column tests pin; the id is fixed so a link and a stored view name the same condition.
 */
export const ARCHIVED_HIDDEN_FILTER: string = JSON.stringify({
  logic: 'and',
  items: [{ id: 'archived-default', field: 'archived', operator: 'isAnyOf', value: ['false'] }],
});

/** The view as parameters, for a link and for storage. An empty field is left out of both. */
export function gridViewParams(state: GridViewState): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = {};
  if (state.search) {
    params.search = state.search;
  }
  if (state.filter) {
    params.filter = state.filter;
  }
  if (state.sort.length > 0) {
    params.sort = state.sort;
  }
  return params;
}

/**
 * The view as a stored entry.
 *
 * Paired with {@link readStoredGridView}, whose `null` means this grid has never been left in a
 * view here — which is *not* the same as being left in an empty one. `gridViewParams` leaves out
 * every empty field, so a user who removes the last condition stores `{}`; read as "nothing
 * stored", the next visit counts as a first one and gets the default filter back — putting back
 * exactly the condition that was just removed. The difference is the entry existing at all, so it
 * cannot be carried by the parameters themselves.
 */
export function storedGridView(state: GridViewState): string {
  return JSON.stringify(gridViewParams(state));
}

/** The stored view, or `null` when nothing readable is stored. */
export function readStoredGridView(raw: string | null): GridViewState | null {
  if (!raw) {
    return null;
  }

  let stored: unknown;
  try {
    stored = JSON.parse(raw);
  } catch {
    return null;
  }

  // Whatever an older build, or a hand-edited entry, left behind: with no view to honour, the
  // first visit's defaults are the safe reading.
  if (typeof stored !== 'object' || stored === null || Array.isArray(stored)) {
    return null;
  }

  return readGridViewState(stored);
}
